import { useCallback, useEffect, useMemo, useState } from "react";
import { Sparkles, ChevronDown, Settings2, RefreshCw, Pin, PinOff, Ban } from "lucide-react";
import { api } from "../lib/api";
import { storeName } from "../lib/labels";
import PeriodBadge from "./PeriodBadge";

function stripPrefix(name) {
  return (name || "").replace(/^\s*#Printed\s*Tee\s*[-–:]\s*/i, "").trim();
}

function nameKey(name) {
  return (name || "").trim().toLowerCase();
}

const SLOT_STYLE = {
  pinned: {
    label: "Pinned",
    chip: "bg-amber-600 text-[#f5f2eb] border-amber-700",
    rail: "border-l-4 border-amber-600",
    tint: "bg-[#f5f2eb]",
  },
  new: {
    label: "New",
    chip: "bg-[#0a0a0a] text-[#f5f2eb] border-[#0a0a0a]",
    rail: "border-l-4 border-[#0a0a0a]",
    tint: "bg-[#f5f2eb]",
  },
  proven: {
    label: "Proven",
    chip: "bg-emerald-800 text-[#f5f2eb] border-emerald-800",
    rail: "border-l-4 border-emerald-800",
    tint: "bg-[#f5f2eb]",
  },
  rising: {
    label: "Rising",
    chip: "bg-[#c72c2c] text-[#f5f2eb] border-[#c72c2c]",
    rail: "border-l-4 border-[#c72c2c]",
    tint: "bg-[#f5f2eb]",
  },
  backfill: {
    label: "Backfill",
    chip: "bg-neutral-500 text-[#f5f2eb] border-neutral-500",
    rail: "border-l-4 border-neutral-400",
    tint: "bg-[#f5f2eb]",
  },
};

function PickRow({ pick, isPinned, onPin, onUnpin, onVeto }) {
  const s = SLOT_STYLE[pick.slot_type] || SLOT_STYLE.backfill;
  const key = nameKey(pick.name);
  return (
    <div
      className={`flex items-center gap-2 py-1.5 pl-2 pr-2 border-b border-[#0a0a0a]/10 group ${s.rail} ${s.tint}`}
      data-testid={`predictor-pick-${pick.slot_number}`}
    >
      <div className="bm-eyebrow font-mono-bm text-neutral-500 w-8 shrink-0">
        #{String(pick.slot_number).padStart(2, "0")}
      </div>
      <span className={`bm-eyebrow px-1.5 py-0.5 border text-[9px] shrink-0 ${s.chip}`}>{s.label}</span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate" title={pick.name}>{stripPrefix(pick.name)}</div>
        <div className="bm-eyebrow text-neutral-500 truncate">{pick.reason}</div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {isPinned ? (
          <button
            onClick={() => onUnpin(key, pick.name)}
            className="p-1 border border-amber-700/50 text-amber-700 hover:bg-amber-100"
            title="Unpin"
            data-testid={`predictor-unpin-${pick.slot_number}`}
          >
            <PinOff size={11} />
          </button>
        ) : (
          <button
            onClick={() => onPin(key, pick.name)}
            className="p-1 border border-[#0a0a0a]/40 hover:bg-amber-100 hover:border-amber-700"
            title="Pin to wall"
            data-testid={`predictor-pin-${pick.slot_number}`}
          >
            <Pin size={11} />
          </button>
        )}
        <button
          onClick={() => onVeto(key, pick.name)}
          className="p-1 border border-[#0a0a0a]/40 hover:bg-red-100 hover:border-[#c72c2c] hover:text-[#c72c2c]"
          title="Veto (hide from wall)"
          data-testid={`predictor-veto-${pick.slot_number}`}
        >
          <Ban size={11} />
        </button>
      </div>
    </div>
  );
}

function BenchRow({ b, index, onPin, onVeto }) {
  const key = nameKey(b.name);
  return (
    <div className="flex items-center gap-3 py-1.5 px-2 border-b border-[#0a0a0a]/10 bg-[#f5f2eb]/50 group">
      <div className="bm-eyebrow font-mono-bm text-neutral-500 w-8 shrink-0">bn{index + 1}</div>
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{stripPrefix(b.name)}</div>
        <div className="bm-eyebrow text-neutral-500 truncate">{b.reason}</div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onPin(key, b.name)}
          className="p-1 border border-[#0a0a0a]/40 hover:bg-amber-100 hover:border-amber-700"
          title="Pin to wall"
          data-testid={`predictor-bench-pin-${index}`}
        >
          <Pin size={11} />
        </button>
        <button
          onClick={() => onVeto(key, b.name)}
          className="p-1 border border-[#0a0a0a]/40 hover:bg-red-100 hover:border-[#c72c2c] hover:text-[#c72c2c]"
          title="Veto"
          data-testid={`predictor-bench-veto-${index}`}
        >
          <Ban size={11} />
        </button>
      </div>
    </div>
  );
}

function VetoedRow({ v, onRestore }) {
  return (
    <div className="flex items-center gap-3 py-1.5 px-2 border-b border-[#c72c2c]/20 bg-[#c72c2c]/5">
      <Ban size={11} className="text-[#c72c2c] shrink-0" />
      <div className="min-w-0 flex-1 text-sm truncate line-through decoration-[#c72c2c]/40">
        {stripPrefix(v.display_name)}
      </div>
      <button
        onClick={() => onRestore(v.name_key)}
        className="bm-eyebrow px-2 py-0.5 border border-[#0a0a0a]/40 hover:bg-[#0a0a0a] hover:text-[#f5f2eb]"
        title="Un-veto"
      >
        restore
      </button>
    </div>
  );
}

function StoreColumn({ store, overrides, onSlotChange, onPin, onUnpin, onVeto, onRestore }) {
  const [showBench, setShowBench] = useState(false);
  const [showVetoed, setShowVetoed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [slotDraft, setSlotDraft] = useState(store.slots);
  useEffect(() => setSlotDraft(store.slots), [store.slots]);

  const short = storeName(store.name);
  const c = store.counts || {};

  const pinnedSet = useMemo(
    () => new Set(overrides.filter((o) => o.outlet_id === store.outlet_id && o.action === "pin").map((o) => o.name_key)),
    [overrides, store.outlet_id],
  );
  const vetoedList = useMemo(
    () => overrides.filter((o) => o.outlet_id === store.outlet_id && o.action === "veto"),
    [overrides, store.outlet_id],
  );

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-4 flex flex-col" data-testid={`predictor-store-${short}`}>
      <div className="flex items-baseline justify-between mb-1">
        <h4 className="bm-headline text-xl">{short}</h4>
        <div className="bm-eyebrow text-neutral-500 font-mono-bm">{c.filled || 0} / {store.slots} slots</div>
      </div>

      {/* Slot editor */}
      <div className="flex items-center gap-2 mb-3">
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="bm-eyebrow text-neutral-500 hover:text-[#0a0a0a] inline-flex items-center gap-1"
            data-testid={`predictor-edit-slots-${short}`}
          >
            <Settings2 size={11} /> edit slot count
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <input
              type="number"
              min="1"
              max="200"
              value={slotDraft}
              onChange={(e) => setSlotDraft(parseInt(e.target.value || "1", 10))}
              className="w-16 px-1 py-0.5 border border-[#0a0a0a] bg-[#f5f2eb] font-mono-bm text-sm"
              data-testid={`predictor-slots-input-${short}`}
            />
            <button
              onClick={() => { onSlotChange(store.name, slotDraft); setEditing(false); }}
              className="bm-eyebrow px-2 py-0.5 bg-[#0a0a0a] text-[#f5f2eb] border border-[#0a0a0a]"
            >
              save
            </button>
            <button
              onClick={() => { setSlotDraft(store.slots); setEditing(false); }}
              className="bm-eyebrow px-2 py-0.5 border border-[#0a0a0a]"
            >
              x
            </button>
          </div>
        )}
      </div>

      {/* Slot-type counts */}
      <div className="grid grid-cols-4 gap-1 mb-3">
        <div className="border-l-4 border-amber-600 pl-2">
          <div className="bm-eyebrow text-neutral-500">Pinned</div>
          <div className="font-mono-bm font-semibold">{c.pinned || 0}</div>
        </div>
        <div className="border-l-4 border-[#0a0a0a] pl-2">
          <div className="bm-eyebrow text-neutral-500">New</div>
          <div className="font-mono-bm font-semibold">{c.new || 0}</div>
        </div>
        <div className="border-l-4 border-emerald-800 pl-2">
          <div className="bm-eyebrow text-neutral-500">Proven</div>
          <div className="font-mono-bm font-semibold">{c.proven || 0}</div>
        </div>
        <div className="border-l-4 border-[#c72c2c] pl-2">
          <div className="bm-eyebrow text-neutral-500">Rising</div>
          <div className="font-mono-bm font-semibold">{c.rising || 0}</div>
        </div>
      </div>

      {/* Picks */}
      <div className="flex-1 overflow-hidden">
        <div className="max-h-[680px] overflow-y-auto border border-[#0a0a0a]/20" data-testid={`predictor-picks-${short}`}>
          {store.picks?.length ? (
            store.picks.map((p) => (
              <PickRow
                key={p.slot_number}
                pick={p}
                isPinned={pinnedSet.has(nameKey(p.name))}
                onPin={(k, n) => onPin(store.outlet_id, k, n)}
                onUnpin={(k) => onUnpin(store.outlet_id, k)}
                onVeto={(k, n) => onVeto(store.outlet_id, k, n)}
              />
            ))
          ) : (
            <div className="text-sm text-neutral-500 p-3">No picks yet.</div>
          )}
        </div>
      </div>

      {/* Bench */}
      {store.bench?.length > 0 && (
        <button
          onClick={() => setShowBench((v) => !v)}
          className="mt-2 bm-eyebrow text-neutral-500 hover:text-[#0a0a0a] flex items-center gap-1"
          data-testid={`predictor-bench-toggle-${short}`}
        >
          <ChevronDown size={12} className={`transition-transform ${showBench ? "rotate-180" : ""}`} />
          {showBench ? "Hide bench" : `Bench · next ${store.bench.length}`}
        </button>
      )}
      {showBench && store.bench?.length > 0 && (
        <div className="mt-2 border border-[#0a0a0a]/20 max-h-[240px] overflow-y-auto">
          {store.bench.map((b, i) => (
            <BenchRow
              key={i}
              b={b}
              index={i}
              onPin={(k, n) => onPin(store.outlet_id, k, n)}
              onVeto={(k, n) => onVeto(store.outlet_id, k, n)}
            />
          ))}
        </div>
      )}

      {/* Vetoed */}
      {vetoedList.length > 0 && (
        <>
          <button
            onClick={() => setShowVetoed((v) => !v)}
            className="mt-2 bm-eyebrow text-[#c72c2c] hover:text-[#0a0a0a] flex items-center gap-1"
            data-testid={`predictor-vetoed-toggle-${short}`}
          >
            <ChevronDown size={12} className={`transition-transform ${showVetoed ? "rotate-180" : ""}`} />
            {showVetoed ? "Hide" : `Show`} vetoed · {vetoedList.length}
          </button>
          {showVetoed && (
            <div className="mt-2 border border-[#c72c2c]/30 max-h-[200px] overflow-y-auto">
              {vetoedList.map((v) => (
                <VetoedRow key={v.name_key} v={v} onRestore={(k) => onRestore(store.outlet_id, k)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function TeePredictor() {
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState({});   // outlet name → override

  async function fetchData() {
    setLoading(true);
    try {
      const params = {};
      if (slots["Public Butter Kensington"]) params.slots_pb = slots["Public Butter Kensington"];
      if (slots["Black Market 347"]) params.slots_347 = slots["Black Market 347"];
      if (slots["Black Market 256"]) params.slots_256 = slots["Black Market 256"];
      const [r, o] = await Promise.all([
        api.get("/products/tee-predictor", { params }),
        api.get("/products/tee-predictor-overrides"),
      ]);
      setData(r.data);
      setOverrides(o.data || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (Object.keys(slots).length > 0) fetchData();
    // eslint-disable-next-line
  }, [slots["Public Butter Kensington"], slots["Black Market 347"], slots["Black Market 256"]]);

  const setOverride = useCallback(async (outlet_id, name_key, action, display_name) => {
    await api.post("/products/tee-predictor-overrides", { outlet_id, name_key, action, display_name });
    await fetchData();
    // eslint-disable-next-line
  }, []);

  const onPin = (outlet_id, name_key, name) => setOverride(outlet_id, name_key, "pin", name);
  const onUnpin = (outlet_id, name_key) => setOverride(outlet_id, name_key, "clear");
  const onVeto = (outlet_id, name_key, name) => setOverride(outlet_id, name_key, "veto", name);
  const onRestore = (outlet_id, name_key) => setOverride(outlet_id, name_key, "clear");

  const stores = data?.stores || [];
  const cfg = data?.config || {};

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6" data-testid="tee-predictor">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
        <div className="bm-eyebrow inline-flex items-center gap-1">
          <Sparkles size={11} />
          Table 15 &middot; This week's wall
        </div>
        <div className="flex items-center gap-3">
          <div className="bm-eyebrow text-neutral-500">
            {data?.generated_at
              ? new Date(data.generated_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
              : ""}
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="bm-eyebrow border border-[#0a0a0a] px-2 py-1 hover:bg-[#0a0a0a]/5 inline-flex items-center gap-1"
            data-testid="predictor-refresh"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
            {loading ? "Scoring…" : "Refresh"}
          </button>
        </div>
      </div>
      <h3 className="bm-headline text-3xl mb-1">Printed Tee predictor.</h3>
      <PeriodBadge label="All history → forecast" note="trained on every design's full sales curve; not filtered by the date picker" className="mb-2" />
      <p className="bm-eyebrow text-neutral-500 mb-4">
        Which designs to feature this week &middot; hover any row to pin or veto for that store
      </p>

      {/* Algorithm summary */}
      <div className="border border-[#0a0a0a]/50 bg-[#f5f2eb] p-3 mb-5 text-sm">
        <div className="bm-eyebrow text-neutral-500 mb-1">How the picks are chosen</div>
        <ul className="list-disc pl-5 space-y-0.5 text-[13px] text-neutral-800">
          <li><strong>Pinned</strong> — manager pins take the top slots first (bypass all filters).</li>
          <li><strong>New</strong> — every design first sold within the last {cfg.new_arrival_weeks || 6} weeks is auto-featured (capped at ~35% of the wall).</li>
          <li><strong>Proven</strong> (60% of remaining) — ranks by retention × store lifetime × freshness. All-time winners.</li>
          <li><strong>Rising</strong> (40% of remaining) — ranks by last-4w velocity z-score × growth rate × store fit. Contenders.</li>
          <li>Vetoed designs are hidden entirely. Diversity cap so the wall isn't monotone; bench shows the next 10 runners-up.</li>
        </ul>
      </div>

      {loading && !data && <div className="text-sm text-neutral-500">Scoring the catalog…</div>}

      {stores.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {stores.map((s) => (
            <StoreColumn
              key={s.outlet_id}
              store={s}
              overrides={overrides}
              onSlotChange={(name, count) => setSlots((prev) => ({ ...prev, [name]: count }))}
              onPin={onPin}
              onUnpin={onUnpin}
              onVeto={onVeto}
              onRestore={onRestore}
            />
          ))}
        </div>
      )}
    </div>
  );
}
